import { Stack } from "@mui/material";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { brandingAtom } from "../brand-kit/store";
import { TemplateCarouselImage } from "./template-carousel-image";
import { TemplateImagesCarousel } from "./template-images-carousel";

import type { Loadable } from "../../utils/jotai-loadable";
import type { LoadableTemplateImage, Template } from "../generic/atoms/types/template";
import type { StackProps } from "@mui/material";
import type { Atom } from "jotai";
import type { FC, ReactElement } from "react";

export const TemplateImages: FC<
  {
    loadableTemplateAtom: Atom<Loadable<Template | null>>;
    onSelect?: (n: number) => void;
    tooltip?: ReactElement;
    carousel?: boolean;
  } & Omit<StackProps, "onSelect">
> = ({ loadableTemplateAtom, onSelect, carousel = false, ...props }) => {
  const template = useAtomValue(loadableTemplateAtom);
  const { initialized } = useAtomValue(brandingAtom);

  // Until branding is initialised, show blank placeholders for as many images as were last shown
  const [images, setImages] = useState<LoadableTemplateImage[]>(() =>
    template?.state === "hasData" && initialized ? (template.data?.images ?? []) : [],
  );
  const numImages = images ? images.length : 0;
  const [prevTemplate, setPrevTemplate] = useState(template);
  const [prevInitialized, setPrevInitialized] = useState(initialized);
  if (template !== prevTemplate || initialized !== prevInitialized) {
    setPrevTemplate(template);
    setPrevInitialized(initialized);
    if (template?.state === "hasData") {
      setImages(
        initialized
          ? (template.data?.images ?? [])
          : Array.from({ length: numImages }, (): LoadableTemplateImage => ({
              state: "hasData",
              data: null,
            })),
      );
    }
  }

  const selectedImage = template?.state === "hasData" ? template.data?.selectedImage : undefined;

  if (carousel)
    return (
      <TemplateImagesCarousel
        images={images}
        selectedImage={selectedImage}
        onSelect={onSelect}
        {...props}
      />
    );

  return (
    <Stack direction="row" gap={2} {...props}>
      {/* oxlint-disable-next-line typescript/no-unsafe-type-assertion */}
      {(images ?? (Array(numImages).fill(undefined) as undefined[])).map((ii, i) => (
        <TemplateCarouselImage
          // oxlint-disable-next-line react/no-array-index-key -- Positional slots; the index is the identity
          key={`img-${i}`}
          image={ii}
          selected={selectedImage === i}
          onClick={() => onSelect?.(i)}
          sx={{
            opacity: selectedImage === i ? 1 : 0.8,
            width: "90px",
            height: "90px",
          }}
        />
      ))}
    </Stack>
  );
};
